/**
 * Performance utilities for optimizing page load times
 */

/**
 * Run a callback when the browser is idle
 */
export const runWhenIdle = (callback: () => void, timeout = 2000) => {
  if (typeof window === 'undefined') return;

  if ('requestIdleCallback' in window) {
    (
      window as Window & {
        requestIdleCallback: (cb: () => void, options?: { timeout: number }) => void;
      }
    ).requestIdleCallback(callback, { timeout });
  } else {
    setTimeout(callback, 100);
  }
};

/**
 * Defer execution until after the page has loaded
 */
export const afterPageLoad = (callback: () => void) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  try {
    if (document.readyState === 'complete') {
      runWhenIdle(callback);
    } else {
      window.addEventListener('load', () => runWhenIdle(callback), { once: true });
    }
  } catch (error) {
    console.error('Error in afterPageLoad:', error);
  }
};

/**
 * Measure and log performance metrics
 */
export const logPerformanceMetrics = () => {
  if (typeof window === 'undefined' || !window.performance) return;

  afterPageLoad(() => {
    try {
      const perfData = window.performance.timing;
      if (!perfData) return;

      const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
      const domContentLoadedTime = perfData.domContentLoadedEventEnd - perfData.navigationStart;
      const timeToFirstByte = perfData.responseStart - perfData.navigationStart;

      if (process.env.NODE_ENV === 'development') {
        console.log('Performance Metrics:', {
          'Page Load Time': `${pageLoadTime}ms`,
          'DOM Content Loaded': `${domContentLoadedTime}ms`,
          'Time to First Byte': `${timeToFirstByte}ms`
        });
      }
    } catch (error) {
      console.error('Error logging performance metrics:', error);
    }
  });
};
